<template>
  <Layout>
    <div data-component="ParcimmoList" :class="`w-full px-4 ${loading ? 'loading' : ''}`">
    <div v-if="!errored">
      <div class="list-header">
        <h3 :class="{ 'animate-blinking': totalLoading }">{{ mapTotalToTitle(total)(totalLoading) }}</h3>
        <FilterChips :query="activeFilters" @remove-filter="onRemoveFilter" />
        <div class="list-header-actions">
          <ButtonFilter 
            :stringifiedFilters="stringifiedFilters" 
            :filtersNumber="filtersNumber" 
            :blink="loading"
            subject="PropertyPortfolio"
          />
          <ButtonExportExcel
            :count="selectedCount"
            :loading="exportLoading"
            :apiAvailable="apiAvailable"
            @export="exportToExcel"
          />
        </div>
      </div>
      <div class="list-wrapper">
        <ListHead 
          :columns="columns"
          :onSort="onSort"
          :loading="loading"
        />
        <ListBody 
          :rows="rows"
          :onAttribute="onAttribute"
          :onPlanify="onPlanify"
          :onResetPlanification="onResetPlanification"
          :onCheck="onCheck"
          :onDelete="onDelete"
          :onPostpone="onPostpone"
          :toggleDropdown="toggleDropdown"
          :closeDropdown="closeDropdown"
        />
      </div>
    </div>

    <div v-if="errored && !hasLocalData" class="no-data-message">
      <div class="text-center p-8">
        <h3 class="text-lg font-semibold mb-4">Aucune donnée disponible</h3>

        <!-- Explication du problème -->
        <div class="text-left bg-gray-50 border border-gray-200 rounded p-4 mb-6 text-sm text-gray-700">
          <p class="font-semibold mb-2">Pourquoi cet écran s'affiche ?</p>
          <ul class="space-y-1">
            <li>• La base de données locale (IndexedDB) ne contient aucune propriété.</li>
            <li>• Cela arrive lors d'une première installation ou si le navigateur a vidé le stockage local.</li>
            <li v-if="isDetectedOffline" class="text-orange-600 font-semibold">
              ⚠️ Connexion réseau non détectée — le chargement depuis le serveur risque d'échouer.
            </li>
            <li v-if="initialLoadingStoreError" class="text-red-600">
              ⚠️ Dernière erreur du chargement initial : <code class="bg-red-50 px-1 rounded">{{ initialLoadingStoreError }}</code>
            </li>
          </ul>
        </div>

        <button 
          @click="loadFromAPI" 
          :disabled="loadingFromAPI"
          class="bg-lightblue hover:bg-lightblue2 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {{ loadingFromAPI ? 'Chargement en cours...' : 'Charger les données depuis le serveur' }}
        </button>

        <!-- Progression du rechargement -->
        <div v-if="loadingFromAPI" class="mt-6 text-left bg-blue-50 border border-blue-200 rounded p-4 text-sm">
          <p class="font-semibold text-blue-800 mb-3">Chargement en cours…</p>
          <p class="text-blue-700 mb-3 font-medium">
            Étape actuelle : <span class="italic">{{ initialLoadingCurrentStep }}</span>
          </p>
          <ul class="space-y-1">
            <li
              v-for="(step, index) in initialLoadingSteps"
              :key="index"
              :class="step.done ? 'text-green-700' : (index === initialLoadingStepIndex ? 'text-blue-700 font-semibold' : 'text-gray-400')"
            >
              <span v-if="step.done">✅</span>
              <span v-else-if="index === initialLoadingStepIndex">⏳</span>
              <span v-else>○</span>
              {{ step.name }}
            </li>
          </ul>
        </div>

        <!-- Erreur survenue pendant le rechargement -->
        <div v-if="loadingAPIError" class="mt-4 text-left bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
          <p class="font-semibold mb-1">Erreur lors du chargement :</p>
          <p>{{ loadingAPIError }}</p>
          <p class="mt-2 text-gray-600">Vérifiez votre connexion réseau et réessayez. Si le problème persiste, contactez l'assistance.</p>
        </div>
      </div>
    </div>

    <WifiError v-if="errored && hasLocalData" />
    
    <div class="bottom-overlay"></div>
    </div>
  </Layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import dictionaryCache from '../services/dictionaryCache'
import dexieService from '../services/dexieService'
import mutationService from '../services/mutationService'
import ButtonFilter from '../components/List/ButtonFilter.vue'
import ButtonExportExcel from '../components/List/ButtonExportExcel.vue'
import apiService from '../services/api'
import FilterChips from '../components/List/FilterChips.vue'
import ListHead from '../components/List/Head.vue'
import ListBody from '../components/List/Body.vue'
import WifiError from '../components/Common/WifiError.vue'
import AppLayout from '../components/Layout/AppLayout.vue'
import { openAttributionModal } from '../utils/attributionHelper'
import { buildUrlWithPreservedParams } from '../utils/urlHelper'
import { saveFilters, loadFilters, getFiltersWithDefaults } from '../services/filterStorageService'
import apiStatusService from '../services/apiStatusService' // conservé pour getStatus() initial
import {
  formatDate,
  isVisitableProperty,
  getVisitStatusValue,
  getScheduledVisitColumn,
  getAttributionVisiteColumn,
  getPlanifiedColumn,
  getDelegationText
} from '../utils/lot'

const SORT_NONE = 'None'
const SORT_ASC = 'Asc'
const SORT_DESC = 'Desc'

export default {
  name: 'ParcimmoListView',
  components: {
    ButtonFilter,
    ButtonExportExcel,
    FilterChips,
    ListHead,
    ListBody,
    WifiError,
    Layout: AppLayout
  },
  props: {
    mapTotalToTitle: {
      type: Function,
      default: (total) => (loading) => loading ? 'Chargement...' : `${total} propriétés`
    }
  },
  data() {
    return {
      loading: false,
      totalLoading: false,
      exportLoading: false,
      errored: false,
      total: 0,
      activeFilters: {},
      stringifiedFilters: '',
      filtersNumber: 0,
      columns: [
        { label: 'Identifiant NSIL', sortId: 'nsil', sortable: true, direction: SORT_NONE, sortDexieId: ['nsil+id'] },
        { label: 'Code postal', sortId: 'zipCode', sortable: true, direction: SORT_NONE, sortDexieId: ['address.zipCode+id'] },
        { label: 'Type', sortId: 'propertyType', sortable: true, direction: SORT_NONE, sortDexieId: ['type+id'] },
        { label: 'Mandant', sortId: 'assistanceMandator', sortable: true, direction: SORT_NONE, sortDexieId: ['assistance.mandator.id+id'] },
        { label: 'Attribution visite', sortId: 'technicalManager', sortable: true, direction: SORT_NONE, sortDexieId: ['scheduledVisitStatusSortAsc+technicalManager.fullName+id'] },
        { label: 'A visiter avant', sortId: 'scheduledVisitAt', sortable: true, direction: 'Asc', sortDexieId: ['scheduledVisitAtSortAsc+scheduledVisitAt+scheduledVisitStatusSortAsc+scheduledVisitStatus+id'] },
        { label: 'Planifiée le', sortId: 'planifiedAt', sortable: true, direction: SORT_NONE, sortDexieId: ['occupancySortDesc+planifiedAtSortDesc+scheduledVisitStatusSortAsc+planifiedAt+id'] }
      ],
      rows: [],
      pagination: {
        page: 1,
        limit: 12,
        total: 0
      },
      progressiveLoadingEnabled: false, // Désactivé pour affichage instantané
      loadingBatchSize: 6,
      loadingDelay: 0,
      firstPageLoadDelay: 0,
      isLoadingMore: false,
      hasMoreData: true,
      scrollThreshold: 250,
      propertyTypes: {},
      visitStatuses: [],
      assistanceMandators: {},
      managerMandators: {},
      hasLocalData: false,
      loadingFromAPI: false,
      loadingAPIError: null,
      apiAvailable: false,
      removeApiListener: null
    }
  },
  computed: {
    ...mapState('list', ['filters']),
    ...mapGetters('attribution', ['lastAttributionUpdate']),
    isDetectedOffline() {
      return this.$store.state.initialLoading?.detectedOffline || this.$store.state.initialLoading?.offline || false
    },
    initialLoadingStoreError() {
      return this.$store.state.initialLoading?.error || null
    },
    initialLoadingCurrentStep() {
      return this.$store.getters['initialLoading/currentStep'] || ''
    },
    initialLoadingSteps() {
      return this.$store.state.initialLoading?.steps || []
    },
    initialLoadingStepIndex() {
      return this.$store.state.initialLoading?.currentStepIndex ?? 0
    },
    selectedCount() {
      const count = this.rows.filter(row => row.checked && !row.loading).length
      return count
    }
  },
  async mounted() {
    // Initialiser le statut de l'API (valeur courante déjà connue)
    // Initialiser le statut de l'API (valeur courante déjà connue)
    this.apiAvailable = apiStatusService.getStatus()

    // S'abonner aux changements de statut API via le bus global
    this.removeApiListener = Event.listen('api-status-change', ({ isAvailable }) => {
      this.apiAvailable = isAvailable
    })

    // Charger les filtres depuis localStorage (avec defaults si première visite)
    const savedRaw = loadFilters('PropertyPortfolio')
    this.activeFilters = getFiltersWithDefaults('PropertyPortfolio')

    // Injecter l'agence de l'utilisateur UNIQUEMENT si la clé 'team' n'existe
    // pas du tout dans les filtres sauvegardés (= jamais défini, pas explicitement vidé).
    // Si team === '' c'est que l'utilisateur a volontairement retiré le filtre agence.
    const teamNeverSet = savedRaw === null || !Object.prototype.hasOwnProperty.call(savedRaw, 'team')
    if (teamNeverSet) {
      try {
        const currentUser = await dexieService.getUser()
        if (currentUser?.team?.id) {
          this.activeFilters = { ...this.activeFilters, team: currentUser.team.id }
          saveFilters('PropertyPortfolio', this.activeFilters)
        }
      } catch (e) {
        // ignore
      }
    }

    // Charger les dictionnaires en parallèle (instantané si déjà en cache)
    await this.loadDictionariesFromCache()

    // Charger les données
    await this.loadItems()

    this.setupScrollListener()
  },
  beforeUnmount() {
    this.removeScrollListener()
    if (this.removeApiListener) this.removeApiListener()
  },
  watch: {
    // Rechargement lors du retour depuis la page de filtres
    '$route'(to) {
      if (to.path === '/parc-immobilier') {
        this.activeFilters = getFiltersWithDefaults('PropertyPortfolio')
        this.loadItems()
      }
    },
    lastAttributionUpdate(newVal) {
      if (newVal) {
        // Recharger les données quand une attribution a été modifiée
        this.loadItems()
      }
    }
  },
  methods: {
    ...mapActions('list', [
      'setItems', 
      'setLoading', 
      'setError', 
      'setPagination',
      'setTotalLoading',
      'setProgressiveLoading',
      'setReady',
      'updateRowLoading'
    ]),

    /**
     * Charge tous les dictionnaires depuis le cache en parallèle
     * Bien plus rapide que le chargement séquentiel
     */
    async loadDictionariesFromCache() {
      try {
        // Initialiser le cache si pas déjà fait, puis charger tous les dictionnaires en parallèle
        await dictionaryCache.initialize()
        
        const [propertyTypesData, visitStatusesData, assistanceMandatorsData, managerMandatorsData] = await Promise.all([
          dictionaryCache.getDictionary('property_type'),
          dictionaryCache.getDictionary('visit_status'),
          dictionaryCache.getAssistanceMandators(),
          dictionaryCache.getManagerMandators()
        ])

        // Convertir property_type en objet clé-valeur
        const typeMap = {}
        if (propertyTypesData && propertyTypesData.items && Array.isArray(propertyTypesData.items)) {
          propertyTypesData.items.forEach(item => {
            if (item.key && item.value) {
              typeMap[item.key] = item.value
            }
          })
        }
        this.propertyTypes = typeMap

        // Garder visitStatuses en format tableau
        this.visitStatuses = visitStatusesData && visitStatusesData.items ? visitStatusesData.items : []

        // Stocker les mandators
        this.assistanceMandators = assistanceMandatorsData || {}
        this.managerMandators = managerMandatorsData || {}

      } catch (error) {
        console.error('Erreur lors du chargement des dictionnaires depuis le cache:', error)
        this.propertyTypes = {}
        this.visitStatuses = []
        this.assistanceMandators = {}
        this.managerMandators = {}
      }
    },

    async loadItems(isLoadMore = false) {
      if (!isLoadMore) {
        this.resetPagination()
        this.loading = true
        this.totalLoading = true
        this.errored = false
      } else {
        if (this.isLoadingMore) return
        this.isLoadingMore = true
      }
      
      try {
        // Supprimer les valeurs vides avant d'envoyer la requête à Dexie.
        // Une valeur '' signifie "volontairement vidé" côté UI, mais pas de filtre côté requête.
        const filters = Object.fromEntries(
          Object.entries(this.activeFilters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        )
        filters.limit = this.pagination.limit

        if (!isLoadMore) {
          this.stringifiedFilters = new URLSearchParams(this.activeFilters).toString()
          // Ne pas comptabiliser les filtres invisibles (ex: excludeType) dans le badge
          const HIDDEN_FILTER_KEYS = ['excludeType']
          this.filtersNumber = Object.keys(this.activeFilters).filter(k => !HIDDEN_FILTER_KEYS.includes(k)).length
        }
        
        let items = []
        let total = 0
        
        if (isLoadMore) {
          // Pour le chargement de plus d'éléments
          const loadingPlaceholders = this.createLoadingPlaceholders(this.pagination.limit)
          this.rows = [...this.rows, ...loadingPlaceholders]
        }
        
        // Utiliser la nouvelle méthode de pagination avec tri optimisé Dexie
        const currentPage = isLoadMore ? Math.floor(this.rows.filter(row => !row.loading).length / this.pagination.limit) + 1 : 1
        
        // Déterminer la colonne et direction de tri actuelle
        const activeColumn = this.columns.find(col => col.direction !== SORT_NONE)
        const sortColumn = activeColumn ? activeColumn.sortId : null
        const sortDirection = activeColumn ? activeColumn.direction : 'Asc'
        
        const result = await dexieService.getPaginatedProperties(
          filters, 
          currentPage, 
          this.pagination.limit,
          sortColumn,
          sortDirection
        )
        
        if (result.data.length > 0) {
          items = result.data
          total = result.total
          this.hasLocalData = true
          this.hasMoreData = result.hasMore
        } else {
          // Vérifier s'il y a des données locales
          this.hasLocalData = await dexieService.hasLocalData()
          
          if (!this.hasLocalData) {
            items = []
            total = 0
            if (!isLoadMore) {
              this.errored = true
              this.setError('Aucune donnée disponible localement')
            }
          } else {
            // Il y a des données mais aucune ne correspond aux filtres
            items = []
            total = 0
            this.hasMoreData = false
          }
        }
        
        if (!isLoadMore) {
          this.total = total
        }
        
        if (isLoadMore) {
          // Nettoyer les placeholders de chargement
          this.rows = this.rows.filter(row => !row.loading)
        }
        
        if (items.length > 0) {
          if (this.progressiveLoadingEnabled) {
            await this.loadItemsProgressively(items, isLoadMore)
          } else {
            const newRows = this.transformItemsToRows(items)
            if (isLoadMore) {
              this.rows = [...this.rows, ...newRows]
            } else {
              this.rows = newRows
            }
          }
        }
        
        if (!isLoadMore) {
          this.pagination = {
            page: 1,
            limit: this.pagination.limit || 12,
            total: total
          }
          
          this.setItems(items)
          this.setPagination(this.pagination)
        } else {
          this.hasMoreData = items.length === this.pagination.limit && this.rows.length < this.total
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        if (!isLoadMore) {
          this.errored = true
          this.hasLocalData = false
          this.setError(error.message)
        } else {
          this.rows = this.rows.filter(row => !row.loading)
        }
      } finally {
        if (!isLoadMore) {
          this.loading = false
          this.totalLoading = false
          this.setLoading(false)
        } else {
          this.isLoadingMore = false
        }
      }
    },

    async loadFromAPI() {
      this.loadingFromAPI = true
      this.loadingAPIError = null
      
      try {
        // Utiliser le module initialLoading pour charger les données depuis l'API
        await this.$store.dispatch('initialLoading/reload')
        
        // Attendre que le chargement soit terminé (ou en erreur)
        const checkLoading = () => {
          return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
              const storeError = this.$store.state.initialLoading?.error
              if (!this.$store.getters['initialLoading/isLoading']) {
                clearInterval(interval)
                if (storeError) {
                  reject(new Error(storeError))
                } else {
                  resolve()
                }
              }
            }, 500)
          })
        }
        
        await checkLoading()
        
        // Recharger les données locales
        await this.loadItems()
        
      } catch (error) {
        console.error('Erreur lors du chargement depuis l\'API:', error)
        this.loadingAPIError = error.message || 'Erreur lors du chargement des données depuis le serveur'
      } finally {
        this.loadingFromAPI = false
      }
    },

    async loadItemsProgressively(items, isLoadMore = false) {
      if (isLoadMore) {
        // Pour le chargement de plus d'éléments, ajouter des placeholders à la fin
        const placeholders = this.createLoadingPlaceholders(items.length)
        this.rows = [...this.rows, ...placeholders]
      } else {
        // Pour le chargement initial, remplacer tout par des placeholders
        this.rows = this.createLoadingPlaceholders(items.length)
      }
      
      // Optimisation: charger la première page complètement sans délai
      const isFirstPage = !isLoadMore
      const firstPageSize = this.pagination.limit // 12 items pour la première page
      
      for (let i = 0; i < items.length; i += this.loadingBatchSize) {
        const batch = items.slice(i, i + this.loadingBatchSize)
        const batchRows = this.transformItemsToRows(batch)
        
        batchRows.forEach((row, index) => {
          let globalIndex
          if (isLoadMore) {
            // Pour le chargement de plus d'éléments, calculer l'index à partir de la fin
            globalIndex = this.rows.length - items.length + i + index
          } else {
            // Pour le chargement initial
            globalIndex = i + index
          }
          
          if (globalIndex < this.rows.length && globalIndex >= 0) {
            this.rows[globalIndex] = row
          }
        })
        
        // Force la réactivité de Vue
        this.$forceUpdate()
        
        // Optimisation: pas de délai pour toute la première page
        const isWithinFirstPage = isFirstPage && (i + this.loadingBatchSize) < firstPageSize
        const hasMoreBatches = i + this.loadingBatchSize < items.length
        
        if (hasMoreBatches) {
          if (isWithinFirstPage) {
            // Pas de délai pour la première page - chargement immédiat
            await this.delay(this.firstPageLoadDelay)
          } else {
            // Délai minimal pour les pages suivantes
            await this.delay(this.loadingDelay)
          }
        }
      }
    },

    createLoadingPlaceholders(count) {
      const placeholders = []
      for (let i = 0; i < count; i++) {
        placeholders.push({
          id: `loading-${i}`,
          checked: false,
          loading: true,
          data: this.createLoadingRowData(),
          dropdown: {
            open: false,
            actions: []
          }
        })
      }
      return placeholders
    },

    createLoadingRowData() {
      const columnCount = this.columns.length
      const loadingData = []
      
      for (let i = 0; i < columnCount; i++) {
        loadingData.push([
          { type: 'TextLoading', value: this.getLoadingPlaceholderClass(i) }
        ])
      }
      
      return loadingData
    },

    getLoadingPlaceholderClass(columnIndex) {
      const classes = ['short', 'medium', 'long', 'short']
      return classes[columnIndex % classes.length] || 'medium'
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    transformItemsToRows(items) {
      return items.map(item => ({
        id: item.id,
        checked: false,
        loading: false,
        propertyTeams: item.teams || [],
        hasUnsyncedData: item.toSync === 1 || false,
        data: this.getRowData(item),
        dropdown: {
          open: false,
          actions: []
        }
      }))
    },

    getRowData(item) {
      return [
        // Colonne 1: Identifiant NSIL
        [
          { type: 'TextBoldBlue', value: item.nsil || item.propertyCode || `Lot ${item.id}`, link: buildUrlWithPreservedParams(`/dossier/${item.id}/timeline`, this.$route) },
          { type: 'Text', value: `Immeuble - ${item.propertyCode || item.buildingCode || ''}` },
          { type: 'Text', value: `Lot - ${item.unitCode || ''}` }
        ],
        // Colonne 2: Code postal
        [
          { type: 'TextBold', value: this.getZipCode(item) },
          { type: 'Text', value: this.getStreet(item) },
          { type: 'TextBold', value: this.getCity(item) }
        ],
        // Colonne 3: Type
        [
          { type: 'TextBold', value: this.getPropertyType(item) },
          { type: 'Text', value: item.occupancy || '' }
        ],
        // Colonne 4: Mandant
        [
          { type: 'TextBold', value: this.getAssistanceMandator(item) },
          { type: 'Text', value: this.getManagerMandator(item) },
          { type: 'Text', value: this.getLegalOwnerWithDivision(item) }
        ],
        // Colonne 5: Attribution visite
        this.getAttributionVisiteColumn(item),
        // Colonne 6: A visiter avant
        this.getScheduledVisitColumn(item),
        // Colonne 7: Planifiée le
        this.getPlanifiedColumn(item)
      ]
    },

    // Méthodes utilitaires pour extraire les données
    getZipCode(item) {
      return item.address?.zipCode || ''
    },

    getStreet(item) {
      return item.address?.street || ''
    },

    getCity(item) {
      return item.address?.city || ''
    },

    getPropertyType(item) {
      const typeCode = item.type || ''
      
      if (this.propertyTypes && this.propertyTypes[typeCode]) {
        return this.propertyTypes[typeCode]
      }
      
      return typeCode || 'Non défini'
    },

    getAssistanceMandator(item) {
      const mandatorId = item.assistance?.mandator?.id
      if (!mandatorId) return item.assistance?.mandator?.name || this.getManagerMandator(item) || '-'
      
      // Chercher le nom dans la table assistanceMandators
      // Clés normalisées en minuscules dans le cache (UUIDs MSSQL peuvent être en majuscules)
      const mandatorName = this.assistanceMandators[mandatorId.toLowerCase()]
        || this.assistanceMandators[mandatorId]
      // Fallback sur le nom stocké directement dans la propriété (ex: sync depuis MSSQL)
      return mandatorName || item.assistance?.mandator?.name || '-'
    },

    getManagerMandator(item) {
      const mandatorId = item.manager?.mandator?.id
      if (!mandatorId) return null
      
      // Chercher le nom et division dans la table managerMandators
      // Clés normalisées en minuscules dans le cache (UUIDs MSSQL peuvent être en majuscules)
      const mandatorData = this.managerMandators[mandatorId.toLowerCase()]
        || this.managerMandators[mandatorId]
      if (!mandatorData) return item.manager?.mandator?.name || null
      
      const name = mandatorData.name || ''
      const division = mandatorData.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getLegalOwnerWithDivision(item) {
      const legalOwner = item.legalOwner
      if (!legalOwner) return null
      
      const name = legalOwner.name || ''
      const division = legalOwner.division || ''
      
      // Si pas de nom, retourner null
      if (!name) return division || null
      
      // Si pas de division, retourner juste le nom
      if (!division) return name
      
      // Si nom et division sont identiques, ne pas répéter
      if (name === division) {
        return name
      }
      
      // Si le nom contient déjà la division, ne pas la répéter
      if (name.includes(division)) {
        return name
      }
      
      // Sinon retourner "Nom - Division"
      return `${name} - ${division}`
    },

    getAttributionVisiteColumn(item) {
      const result = getAttributionVisiteColumn(item)
      
      return result
    },

    getScheduledVisitColumn(item) {
      const result = getScheduledVisitColumn(item, this.visitStatuses)
      
      return result
    },

    getPlanifiedColumn(item) {
      return getPlanifiedColumn(item)
    },

    isVisitableProperty(scheduledVisitStatus) {
      return isVisitableProperty(scheduledVisitStatus)
    },

    getDelegationText(item) {
      return getDelegationText(item)
    },

    getVisitStatusValue(scheduledVisitStatus) {
      return getVisitStatusValue(scheduledVisitStatus, this.visitStatuses)
    },

    formatDate(date) {
      return formatDate(date)
    },

    onSort(direction) {
      return (sortId) => {
        // Mettre à jour l'état des colonnes
        this.columns = this.columns.map(col => ({
          ...col,
          direction: col.sortId === sortId ? direction : SORT_NONE
        }))
        
        // Recharger les données avec le nouveau tri (utilise Dexie)
        this.loadItems()
      }
    },

    // Méthode de tri côté client conservée pour compatibilité (fallback)
    sortRows(sortId, direction) {
      this.rows.sort((a, b) => {
        let multiplier = 1
        if (direction === SORT_DESC) {
          multiplier = -1
        } else if (direction === SORT_ASC) {
          multiplier = 1
        }
        
        // Récupérer les propriétés originales depuis les données
        const propertyA = this.getPropertyFromRow(a)
        const propertyB = this.getPropertyFromRow(b)
        
        if (!propertyA || !propertyB) {
          return 0
        }
        
        let valueA, valueB
        
        switch (sortId) {
          case 'scheduledVisitAt':
            // Logique exacte de l'ancienne app React
            valueA = this.getScheduledVisitSortValue(propertyA)
            valueB = this.getScheduledVisitSortValue(propertyB)
            break
            
          case 'nsil':
            valueA = propertyA.nsil || ''
            valueB = propertyB.nsil || ''
            break
            
          case 'zipCode':
            valueA = propertyA.address?.zipCode || ''
            valueB = propertyB.address?.zipCode || ''
            break
            
          case 'propertyType':
            valueA = this.getPropertyType(propertyA)
            valueB = this.getPropertyType(propertyB)
            break
            
          case 'assistanceMandator':
            valueA = this.getAssistanceMandator(propertyA)
            valueB = this.getAssistanceMandator(propertyB)
            break
            
          case 'technicalManager':
            valueA = this.getTechnicalManagerSortValue(propertyA)
            valueB = this.getTechnicalManagerSortValue(propertyB)
            break
            
          case 'planifiedAt':
            valueA = this.getPlanifiedSortValue(propertyA)
            valueB = this.getPlanifiedSortValue(propertyB)
            break
            
          default:
            return 0
        }
        
        // Comparaison des valeurs
        if (valueA < valueB) return -1 * multiplier
        if (valueA > valueB) return 1 * multiplier
        
        // En cas d'égalité, trier par ID
        if (propertyA.id < propertyB.id) return -1
        if (propertyA.id > propertyB.id) return 1
        return 0
      })
    },

    // Récupérer la propriété originale depuis une ligne
    getPropertyFromRow(row) {
      // Chercher dans les données chargées
      const allProperties = this.$store.getters['list/items'] || []
      return allProperties.find(prop => prop.id === row.id)
    },

    // Logique de tri pour "A visiter avant" (utilise les valeurs pré-calculées)
    getScheduledVisitSortValue(property) {
      // Utiliser les valeurs pré-calculées si disponibles
      if (property.scheduledVisitAtSortAsc !== undefined) {
        return property.scheduledVisitAtSortAsc
      }
      
      // Fallback: Priorité 1: scheduledVisitStatus transformé
      if (property.scheduledVisitStatus) {
        const transformedStatus = this.getVisitStatusValue(property.scheduledVisitStatus)
        if (transformedStatus && transformedStatus !== property.scheduledVisitStatus) {
          // Utiliser une valeur numérique pour le tri des statuts
          return this.getStatusSortOrder(transformedStatus)
        }
      }
      
      // Fallback: Priorité 2: scheduledVisitAt comme date
      if (property.scheduledVisitAt) {
        return new Date(property.scheduledVisitAt).getTime()
      }
      
      // Pas de valeur de tri - mettre à la fin
      return Number.MAX_SAFE_INTEGER
    },

    // Ordre de tri pour les statuts de visite (utilise le dictionnaire visit_status)
    getStatusSortOrder(statusValue) {
      // Utiliser l'ordre défini dans le dictionnaire visit_status
      if (this.visitStatuses && Array.isArray(this.visitStatuses)) {
        const statusIndex = this.visitStatuses.findIndex(item => item.value === statusValue)
        return statusIndex !== -1 ? statusIndex + 1 : 999
      }
      
      // Fallback avec valeurs statiques si le dictionnaire n'est pas disponible
      const statusOrder = {
        'En attente': 1,
        'Planifiée': 2,
        'En cours': 3,
        'Terminée': 4,
        'Annulée': 5
      }
      
      return statusOrder[statusValue] || 999
    },

    // Valeur de tri pour le gestionnaire technique
    getTechnicalManagerSortValue(property) {
      if (!this.isVisitableProperty(property.scheduledVisitStatus)) {
        return 'zzz' // Mettre à la fin
      }
      
      if (property.technicalManager?.fullName) {
        return property.technicalManager.fullName.toLowerCase()
      }
      
      if (property.technicalManager?.firstName && property.technicalManager?.lastName) {
        return `${property.technicalManager.firstName} ${property.technicalManager.lastName}`.toLowerCase()
      }
      
      return 'zzz' // Pas de gestionnaire - mettre à la fin
    },

    // Valeur de tri pour la planification
    getPlanifiedSortValue(property) {
      if (!this.isVisitableProperty(property.scheduledVisitStatus)) {
        return Number.MAX_SAFE_INTEGER // Mettre à la fin
      }
      
      if (property.planifiedAt) {
        return new Date(property.planifiedAt).getTime()
      }
      
      return Number.MAX_SAFE_INTEGER // Pas planifié - mettre à la fin
    },

    onAttribute(propertyId) {
      return () => {
        
        // Gérer l'asynchrone à l'intérieur de la fonction synchrone
        this.handleAttributeAsync(propertyId)
      }
    },

    async handleAttributeAsync(propertyId) {
      try {
        // Récupérer l'utilisateur courant depuis dexieService
        const currentUser = await dexieService.getUser()
        
        // Ouvrir le modal d'attribution
        openAttributionModal(this.$store, {
          lots: [propertyId],
          teamId: currentUser?.team?.id || null,
          managerId: currentUser?.id || null
        })
        
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur courant:', error)
        // Fallback: ouvrir le modal sans paramètres utilisateur
        openAttributionModal(this.$store, {
          lots: [propertyId]
        })
      }
    },

    onPlanify(propertyId) {
      return async (date) => {
        try {
          // Date déjà au format YYYY-MM-DD depuis l'input date HTML
          const planifiedAt = date || ''
          
          // Utiliser le mutation service pour gérer l'API et le stockage de mutation
          const result = await mutationService.updatePropertyPlanification(propertyId, planifiedAt)
          
          if (result.success) {
            // Mettre à jour l'affichage sans recharger toute la page
            const rowIndex = this.rows.findIndex(row => row.id === propertyId)
            if (rowIndex !== -1) {
              // Récupérer la propriété mise à jour depuis IndexedDB
              const updatedProperty = await dexieService.getProperty(propertyId)
              
              if (updatedProperty) {
                // Marquer la ligne comme ayant des données non synchronisées si pas encore synced
                this.rows[rowIndex].hasUnsyncedData = !result.synced
                
                // Mettre à jour la colonne "Planifiée le" (index 6)
                this.rows[rowIndex].data[6] = this.getPlanifiedColumn(updatedProperty)
                
                // Forcer la réactivité de Vue
                this.$forceUpdate()
              }
            }
          } else {
            console.error('Erreur lors de la planification:', result.error)
          }
          
        } catch (error) {
          console.error('Erreur lors de la planification:', error)
        }
      }
    },

    onResetPlanification(propertyId) {
      return async () => {
        try {
          // Utiliser le mutation service pour gérer l'API et le stockage de mutation
          const result = await mutationService.updatePropertyPlanification(propertyId, '')
          
          if (result.success) {
            // Mettre à jour l'affichage sans recharger toute la page
            const rowIndex = this.rows.findIndex(row => row.id === propertyId)
            if (rowIndex !== -1) {
              // Récupérer la propriété mise à jour depuis IndexedDB
              const updatedProperty = await dexieService.getProperty(propertyId)
              
              if (updatedProperty) {
                // Marquer la ligne comme ayant des données non synchronisées si pas encore synced
                this.rows[rowIndex].hasUnsyncedData = !result.synced
                
                // Mettre à jour la colonne "Planifiée le" (index 6)
                this.rows[rowIndex].data[6] = this.getPlanifiedColumn(updatedProperty)
                
                // Forcer la réactivité de Vue
                this.$forceUpdate()
              }
            }
          } else {
            console.error('Erreur lors de la réinitialisation:', result.error)
          }
          
        } catch (error) {
          console.error('Erreur lors de la réinitialisation de la planification:', error)
        }
      }
    },

    onCheck(id) {
      return () => {
        this.rows = this.rows.map(row => 
          row.id === id ? { ...row, checked: !row.checked } : row
        )
      }
    },

    onDelete() {
      return () => {
        // TODO: Implement delete logic
      }
    },

    onPostpone() {
      return () => {
        // TODO: Implement postpone logic
      }
    },

    toggleDropdown(id) {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: row.id === id ? !row.dropdown.open : false
          }
        }))
      }
    },

    closeDropdown() {
      return () => {
        this.rows = this.rows.map(row => ({
          ...row,
          dropdown: {
            ...row.dropdown,
            open: false
          }
        }))
      }
    },

    // Méthodes pour la pagination par scroll infini
    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll)
    },

    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      const distanceFromBottom = documentHeight - (scrollTop + windowHeight)

      if (distanceFromBottom < this.scrollThreshold && this.shouldLoadMore()) {
        this.loadItems(true)
      }
    },

    shouldLoadMore() {
      return (
        !this.isLoadingMore && 
        !this.loading && 
        this.hasMoreData && 
        this.rows.length < this.total &&
        this.rows.filter(row => !row.loading).length > 0
      )
    },


    /**
     * Supprime un filtre depuis les chips en retirant la clé de la query URL
     * et en mettant à jour le cookie
     */
    onRemoveFilter(key) {
      const newFilters = { ...this.activeFilters }

      if (key === 'team') {
        // Pour 'team' : sauvegarder une chaîne vide pour marquer
        // "l'utilisateur a volontairement retiré ce filtre" (≠ jamais défini).
        newFilters[key] = ''
      } else {
        delete newFilters[key]
      }

      // Mettre à jour les filtres en mémoire et dans localStorage
      this.activeFilters = newFilters
      saveFilters('PropertyPortfolio', newFilters)

      // Recharger la liste avec les nouveaux filtres (sans toucher à l'URL)
      this.loadItems()
    },

    resetPagination() {
      this.rows = []
      this.pagination.page = 1
      this.hasMoreData = true
      this.isLoadingMore = false
    },

    async exportToExcel() {
      if (this.exportLoading) {
        console.warn('[ParcimmoList] exportToExcel() annulé → export déjà en cours')
        return
      }

      const checkedRows = this.rows.filter(row => row.checked && !row.loading)

      let ids
      if (checkedRows.length > 0) {
        // Des lignes sont cochées → exporter uniquement celles-ci
        ids = checkedRows.map(row => row.id)
      } else {
        // Aucune sélection → exporter TOUS les lots filtrés (pas seulement ceux affichés)
        const filters = Object.fromEntries(
          Object.entries(this.activeFilters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        )
        const activeColumn = this.columns.find(col => col.direction !== 'None')
        const sortColumn = activeColumn ? activeColumn.sortId : null
        const sortDirection = activeColumn ? activeColumn.direction : 'Asc'

        ids = await dexieService.getAllFilteredIds(filters, sortColumn, sortDirection)
      }


      this.exportLoading = true
      try {
        const { blob, exportFormat } = await apiService.exportParcImmoExcel(ids)

        // L'API bascule automatiquement sur CSV quand les lots > 1 000
        const ext = exportFormat === 'csv' ? 'csv' : 'xlsx'
        const date  = new Date().toISOString().slice(0, 10)

        const url  = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href     = url
        link.download = `parc-immobilier-export_${date}.${ext}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('[ParcimmoList] exportToExcel() → erreur ❌', error)
        alert('Une erreur est survenue lors de l\'export. Veuillez réessayer.')
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>

<style lang="scss">
@use './../assets/styles/components/List.scss';
</style>
